import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, HeartHandshake, Loader2, MapPin, X } from "lucide-react";

// CUS-11: re-homing a pet through NGOs near the customer.
export const REASONS = [
  ["moving", "Moving home"], ["allergy", "Allergy in the family"], ["time", "Not enough time"],
  ["cost", "Can't afford care"], ["behaviour", "Behaviour I can't manage"], ["health", "My health"], ["other", "Other"],
];
const day = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);

/** "Find a new home": what goes to NGOs near the customer, and why. */
export function RehomeDialog({ pet, vaccines, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [about, setAbout] = useState("");
  const [addresses, setAddresses] = useState(null);
  const [addressId, setAddressId] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [share, setShare] = useState(true);       // health history goes with the pet
  const [byWhen, setByWhen] = useState("");
  const fileRef = useRef(null);
  useEffect(() => {
    api.get("/customer/addresses").then(({ data }) => {
      const rows = (data || []).filter((a) => a.lat != null && !a.needs_location);
      setAddresses(rows);
      setAddressId((rows.find((a) => a.is_default) || rows[0] || {}).id || "");
    }).catch(() => setAddresses([]));
  }, []);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    form.append("purpose", "pet_photo");
    try {
      const { data } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotos((p) => [...p, data.url].slice(0, 5));
    } catch (e2) { err(e2); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/community/me/rehoming", { pet_id: pet.id, address_id: addressId, reason, about: about.trim() || null, photo_urls: photos,
                                                                  share_health: share, by_when: byWhen || null });
      toast.success(data.ngos_told ? `Sent to ${data.ngos_told} NGO${data.ngos_told > 1 ? "s" : ""} near you. We'll tell you when they offer.` : "Posted. We'll tell you when an NGO near you offers.");
      onDone();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rehome-dialog">
        <DialogHeader><DialogTitle>Find a new home for {pet.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-600">Verified animal NGOs near you see {pet.name} and can offer to take them in. You choose who, or change your mind any time before the hand-over.</p>
        <div className="space-y-4 mt-2">
          <div>
            <Label>Why are you looking for a new home?</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {REASONS.map(([k, label]) => (
                <button key={k} type="button" onClick={() => setReason(k)} data-testid={`rehome-reason-${k}`}
                  className={`px-3 h-9 rounded-full border text-sm ${reason === k ? "bg-teal text-white border-teal" : "border-stone-300 hover:border-stone-400"}`}>{label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="rehome-about">About {pet.name} (optional)</Label>
            <Textarea id="rehome-about" data-testid="rehome-about" value={about} maxLength={1000} rows={3} onChange={(e) => setAbout(e.target.value)}
              placeholder="Temperament, habits, food, good with children or other pets…" className="mt-1" />
          </div>
          <div>
            <Label>More photos (optional)</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {[pet.photo_url, ...photos].filter(Boolean).map((u) => (
                <div key={u} className="relative">
                  <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="h-16 w-16 rounded-xl object-cover" />
                  {u !== pet.photo_url && <button type="button" onClick={() => setPhotos((p) => p.filter((x) => x !== u))} aria-label="Remove photo"
                    className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-white shadow flex items-center justify-center"><X className="h-3 w-3" /></button>}
                </div>
              ))}
              {photos.length < 5 && (
                <button type="button" onClick={() => fileRef.current?.click()} className="h-16 w-16 rounded-xl border border-dashed border-stone-300 flex items-center justify-center text-stone-500" data-testid="rehome-photo-add">
                  <Camera className="h-5 w-5" />
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />
            </div>
          </div>
          <div>
            <Label>Where is {pet.name} now?</Label>
            {addresses === null ? <Loader2 className="h-4 w-4 animate-spin mt-2" /> : addresses.length === 0 ? (
              <p className="text-sm text-terracotta mt-1">Add a saved address with a map pin first (Account → Addresses).</p>
            ) : (
              <select value={addressId} onChange={(e) => setAddressId(e.target.value)} data-testid="rehome-address"
                className="mt-1 h-10 w-full rounded-xl border border-stone-300 px-2 text-sm bg-white">
                {addresses.map((a) => <option key={a.id} value={a.id}>{a.label}: {[a.flat_no, a.address].filter(Boolean).join(", ").slice(0, 50)}</option>)}
              </select>
            )}
            <p className="text-xs text-stone-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> NGOs only see the area, not your address.</p>
          </div>
          <div>
            <Label htmlFor="rehome-by">By when? (optional)</Label>
            <input id="rehome-by" type="date" min={new Date().toISOString().slice(0, 10)} value={byWhen} onChange={(e) => setByWhen(e.target.value)}
              className="mt-1 h-10 w-full rounded-xl border border-stone-300 px-2 text-sm bg-white" data-testid="rehome-by" />
          </div>
          <label className="flex items-start gap-2 text-sm rounded-xl border border-stone-200 p-3">
            <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} className="mt-1" data-testid="rehome-share-health" />
            <span><b>Share {pet.name}'s health history with the new home</b><br />
              <span className="text-xs text-stone-600">Allergies, medicines, surgeries, past vet visits and follow-ups due. No bills, no clinic names, and never your name or details. It goes at the hand-over.</span></span>
          </label>
          <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-xs text-stone-600" data-testid="rehome-shared">
            <b>What NGOs see:</b> {pet.name}'s details{vaccines ? `, ${vaccines} vaccination record${vaccines > 1 ? "s" : ""}` : ""}, the photos and what you write.
            Your name and mobile go only to the NGO you choose, so they can arrange the hand-over.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !reason || !addressId} data-testid="rehome-submit" className="rounded-full bg-marigold text-stoneWarm hover:bg-marigold/90 font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ask NGOs near me"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Where a pet's re-homing request stands: offers to choose from, or who's taking them. */
export function RehomeStatus({ req, petName, onChanged }) {
  const [busy, setBusy] = useState("");
  const act = async (path, body, msg) => {
    setBusy(path);
    try { await api.post(`/community/me/rehoming/${req.id}/${path}`, body); toast.success(msg); onChanged(); }
    catch (e) { err(e); } finally { setBusy(""); }
  };
  const offers = (req.offers || []).filter((o) => ["offered", "chosen"].includes(o.status));
  const chosen = offers.find((o) => o.status === "chosen");
  // Vets, care providers and shops: "know an adopter" and "free health check" (Module 3b).
  const biz = (req.business_offers || []).filter((o) => ["offered", "chosen", "accepted"].includes(o.status));
  const chosenBiz = (req.business_offers || []).find((o) => o.id === req.chosen_offer_id);
  return (
    <div className="mt-3 rounded-xl border border-teal/30 bg-teal/5 p-3 text-sm" data-testid={`rehome-status-${req.id}`}>
      <div className="flex items-center gap-2 font-semibold text-stoneWarm"><HeartHandshake className="h-4 w-4 text-teal" />
        {req.status === "open" ? `Looking for a new home · open until ${day(req.expires_at)}`
          : req.status === "matched" ? `Going to ${chosen?.ngo_name || `${chosenBiz?.adopter_first_name} (through ${chosenBiz?.biz_name})`}` : `Request closed on ${day(req.expired_at || req.updated_at)}`}
      </div>
      {biz.length > 0 && (
        <div className="mt-2 space-y-2" data-testid="rehome-biz-offers">
          {biz.map((o) => (
            <div key={o.id} className="rounded-lg bg-white border border-stone-200 p-2.5 flex items-start gap-3" data-testid={`rehome-biz-${o.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{o.biz_name} <span className="text-xs text-stone-500 font-normal">· {o.kind === "health_check" ? "free health check" : `knows an adopter, ${o.adopter_first_name}`}{o.distance_km != null ? ` · ${o.distance_km} km` : ""}</span></div>
                <div className="text-xs text-stone-600 mt-0.5">“{o.message}”</div>
              </div>
              {req.status === "open" && o.kind === "adopter" && o.status === "offered" && (
                <Button size="sm" disabled={!!busy} className="rounded-full bg-stoneWarm text-white" data-testid={`rehome-choose-biz-${o.id}`}
                  onClick={() => window.confirm(`Give ${petName} to ${o.adopter_first_name}? ${o.biz_name} gets your name and mobile to set up the meeting.`) && act("choose-offer", { offer_id: o.id }, `${o.biz_name} will contact you`)}>Choose</Button>
              )}
              {o.kind === "health_check" && (o.status === "accepted" ? <span className="text-xs text-teal font-semibold">They'll call you</span>
                : ["open", "matched"].includes(req.status) && <Button size="sm" variant="outline" disabled={!!busy} className="rounded-full" onClick={() => act("health-check", { offer_id: o.id }, `${o.biz_name} will call you`)}>Yes, please</Button>)}
            </div>
          ))}
        </div>
      )}
      {req.status === "matched" && chosenBiz && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="rehome-adopter-actions">
          <Button size="sm" disabled={!!busy} className="rounded-full bg-stoneWarm text-white" onClick={() => window.confirm(`Is ${petName} with ${chosenBiz.adopter_first_name} now? ${petName} will leave My pets.`) && act("handed-over", {}, `${petName} is with ${chosenBiz.adopter_first_name} now`)}>I handed {petName} over</Button>
          <Button size="sm" variant="outline" disabled={!!busy} className="rounded-full" onClick={() => { const why = window.prompt("What happened? The business is told."); if (why && why.trim().length >= 3) act("not-working", { reason: why.trim() }, "Your request is open again"); }}>It didn't work out</Button>
        </div>
      )}
      {req.status === "open" && (offers.length === 0 ? (
        !biz.length && <p className="text-xs text-stone-600 mt-1">NGOs, vets and pet businesses near you can see {petName}. We'll tell you as soon as one offers.</p>
      ) : (
        <div className="mt-2 space-y-2" data-testid="rehome-offers">
          <p className="text-xs text-stone-600">{offers.length} NGO{offers.length > 1 ? "s" : ""} offered to take {petName}. Choose one:</p>
          {offers.map((o) => (
            <div key={o.ngo_id} className="rounded-lg bg-white border border-stone-200 p-2.5 flex items-start gap-3" data-testid={`rehome-offer-${o.ngo_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium">{o.ngo_name} <span className="text-xs text-stone-500 font-normal">· {o.ngo_city}{o.distance_km != null ? ` · ${o.distance_km} km` : ""} · verified NGO</span></div>
                <div className="text-xs text-stone-600 mt-0.5">“{o.message}”</div>
              </div>
              <Button size="sm" disabled={!!busy} data-testid={`rehome-choose-${o.ngo_id}`} className="rounded-full bg-stoneWarm text-white"
                onClick={() => window.confirm(`Give ${petName} to ${o.ngo_name}? They'll get your name and mobile to arrange the hand-over.`) && act("choose", { ngo_id: o.ngo_id }, `${o.ngo_name} will contact you`)}>
                Choose
              </Button>
            </div>
          ))}
        </div>
      ))}
      {req.status === "matched" && chosen && <p className="text-xs text-stone-600 mt-1">{chosen?.ngo_name} has your name and mobile and will call to arrange the hand-over. {petName} leaves My pets once they've collected them.</p>}
      {req.status === "expired" && <p className="text-xs text-stone-600 mt-1">Nobody was chosen before it closed. You can post it again.</p>}
      <div className="mt-2 flex gap-2">
        {req.status === "expired" && <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} data-testid="rehome-repost" onClick={() => act("repost", {}, "Posted again")}>Post again</Button>}
        {["open", "matched"].includes(req.status) && (
          <Button size="sm" variant="ghost" className="rounded-full text-terracotta" disabled={!!busy} data-testid="rehome-withdraw"
            onClick={() => window.confirm(`Withdraw the request? ${petName} stays with you.`) && act("withdraw", {}, "Request withdrawn")}>
            Withdraw request
          </Button>
        )}
      </div>
    </div>
  );
}
